import React, { useCallback, useEffect, useMemo, useState } from "react";

import { makePatch, makePost } from "@culturehq/client";

import { ApprovedForSocial, PartialPromptQuestion, Story, StoryQuestion } from "chq";
import { Button, classnames, Form, Icon, ModalDialog, NumberField, SelectField, StringField, SubmitButton, TextField } from "@culturehq/components";

import { useTryCatch } from "app/hooks";

import { errorService, t, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";
import ErrorMessage from "components/common/ErrorMessage";
import { MagicWandIcon } from "components/common/icons";

import magicWandIcon from "images/magic-wand_gradient.svg";
import { allowedMedia } from "./SubPromptModal";
import AIQuestionModal from "./AIQuestionModal";
import AIQuestionOptions from "./AIQuestionOptions";
import styles from "./newAIStoryQuestionModal.module.scss";
import AIStoryQuestionLoadingForm from "./AIStoryQuestionLoadingForm";
import ApproveForJobMappingButton from "../stories/ApproveForJobMappingButton";

const Title = ({ storyQuestion }: any) => (storyQuestion
  ? <Trans>Edit AI Story Prompt</Trans>
  : <Trans>Create new AI Story Prompt</Trans>);

export const SaveButton = ({ storyQuestion }: any) => (storyQuestion
  ? <Trans>Edit AI Story Prompt</Trans>
  : <Trans>Create AI Story Prompt</Trans>);

interface Props {
  storyQuestion?: StoryQuestion;
  initialPromptDescription: string;
  openModal: boolean;
  onRestart: () => void;
  onCreate?: (storyQuestion: StoryQuestion) => void;
  onUpdate?: (storyQuestion: StoryQuestion) => void;
}

const AIStoryQuestionModalForm: React.FC<Props> = ({
  storyQuestion,
  initialPromptDescription,
  openModal,
  onRestart,
  onCreate,
  onUpdate
}) => {
  const [changed, setChanged] = useState(!storyQuestion);
  const [error, setError] = useState<string>();
  const [chatId, setChatId] = useState<string>();
  const [promptDescription, setPromptDescription] = useState<string>("");
  const [prompt, setPrompt] = useState<string | undefined>();
  const [summary, setSummary] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<PartialPromptQuestion[]>(
    storyQuestion?.promptQuestions || []
  );
  const filteredQuestions = useMemo(() => questions.filter(q => !q.disabled), [questions]);
  const isFilteredQuestionsEmpty = !filteredQuestions.length;
  const allowAddQuestion = filteredQuestions.length <= 4 && chatId;
  const isEditing = !!storyQuestion;
  const [gamificationValue, setGamificationValue] = useState<number>(50);
  const [mappersValueIds, setMappersValueIds] = useState<string[]>([]);
  const GAMIFICATION_MIN = 0;
  const GAMIFICATION_MAX = 1000;

  const promptQuestionMapping = (questions: any[]) => questions.map(
    ({ title: question, instructions }) => ({ question, instructions }));

  useEffect(() => {
    if (!openModal) return;

    setPromptDescription(initialPromptDescription);
    if (!storyQuestion) return;

    setGamificationValue(storyQuestion.points || 50);
    setPrompt(storyQuestion.question);
    setSummary(storyQuestion.summary);
    setQuestions(storyQuestion.promptQuestions || []);
    setChatId(storyQuestion.chatId);
    setMappersValueIds(storyQuestion.externalMapperValueAssociations.map(
      mapperValueAssocaition => (`${mapperValueAssocaition.externalMapperValue.id}`)
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyQuestion, openModal]);

  useEffect(() => {
    if (!openModal || isEditing) return;

    setLoading(true);
    setError(undefined);
    setPromptDescription(initialPromptDescription);
    makePost("/ai/prompts", { goal: initialPromptDescription })
      .then(({ chatId, prompt: { title, instructions, questions } }: any) => {
        setChatId(chatId);
        setPrompt(title);
        setSummary(instructions);
        setQuestions(promptQuestionMapping(questions));
        setLoading(false);
      })
      .catch(error => {
        setError(error);
        setLoading(false);
        errorService.error(error);
      });
  }, [initialPromptDescription, isEditing, openModal]);

  const restartState = useCallback(() => {
    setGamificationValue(50);
    setChanged(false);
    setError(undefined);
    setChatId("");
    setPromptDescription("");
    setPrompt(undefined);
    setSummary(undefined);
    setLoading(false);
    setQuestions([]);
    setMappersValueIds([]);
    onRestart();
  }, [onRestart]);

  const onChange = useCallback(() => {
    setChanged(true);
  }, [setChanged]);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the prompt modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) restartState();
    },
    [changed, restartState]
  );

  const [{ error: tryCatchError }, handleSubmit] = useTryCatch(async ({
    allowedTypes,
    promptDescription,
    prompt: formPrompt,
    summary: formSummary,
    points,
    visibility,
    status
  }: any) => {
    const body: any = {
      allowedTypes,
      goal: promptDescription,
      mappersValueIds,
      question: formPrompt || prompt,
      summary: formSummary || summary,
      points,
      visibility,
      status,
      chatId,
      questions: questions.map((q, idx) => ({
        ...q,
        position: q.position !== undefined ? q.position : idx
      }))
    };

    if (onUpdate && storyQuestion) {
      const response: any = await makePatch(`/story_questions/${storyQuestion.id}`, body);
      onUpdate(response.storyQuestion);
    } else if (onCreate) {
      const response: any = await makePost("/prompts", body);
      onCreate(response.storyQuestion);
    }

    restartState();
  }, [mappersValueIds, questions, prompt, summary, chatId]);

  if (tryCatchError && tryCatchError.status !== 422) {
    setError(tryCatchError);
    errorService.error(tryCatchError);
  }

  const handleNewQuestion = (question: any) => {
    onChange();
    setQuestions([...questions, question]);
  };

  const handleEditQuestion = (question: any, index: number) => {
    setQuestions((prevQuestions: PartialPromptQuestion[]) => {
      const newQuestions = [...prevQuestions];
      const currentQuestion = newQuestions[index];
      newQuestions[index] = { ...currentQuestion, ...question };
      return newQuestions;
    });
    onChange();
  };

  const handleRemoveQuestion = (index: number) => {
    onChange();
    const newQuestions = [...questions];
    if (isEditing) newQuestions[index].disabled = true;
    else newQuestions.splice(index, 1);
    setQuestions(newQuestions);
  };

  const handleReloadPrompt = async () => {
    if (!chatId) return;

    setLoading(true);
    setError(undefined);
    const disabledQuestions = disablePromptQuestions();
    const { prompt: { title, instructions, questions } }: any = await makePost("/ai/reload_prompts", { chatId });
    const allQuestions = [...promptQuestionMapping(questions), ...disabledQuestions];
    setQuestions(allQuestions);
    setPrompt(title);
    setSummary(instructions);
    setLoading(false);
  };

  const disablePromptQuestions = () => [...questions].filter(
    q => q.id).map(q => ({ ...q, disabled: true }));

  const handleReloadQuestions = async () => {
    if (!chatId) return;

    setLoading(true);
    setError(undefined);
    const disabledQuestions = disablePromptQuestions();
    const { questions }: any = await makePost("/ai/reload_prompt_questions", { chatId });
    const allQuestions = [...promptQuestionMapping(questions), ...disabledQuestions];
    setQuestions(allQuestions);
    setLoading(false);
  };

  const handleSortQuestions = (sortedQuestions: PartialPromptQuestion[]) => {
    setQuestions(sortedQuestions);
  };

  const handleSelectedJobMappings = (value: Record<string, string[]>) => {
    setMappersValueIds(value.mappersValueIds);
  };

  return (
    <>
      {openModal && (
        <ChqModalDialog className="AIPromptModal AIPromptModal-SecondStep" onClose={onConfirmClose}>
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Title storyQuestion={storyQuestion} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {loading ? (
              <AIStoryQuestionLoadingForm
                promptDescription={promptDescription}
                storyQuestion={storyQuestion}
              />
            ) : (
              <Form
                onSubmit={handleSubmit}
                initialValues={
                  {
                    allowedTypes: storyQuestion?.allowedTypes || "all",
                    promptDescription,
                    points: storyQuestion?.points || gamificationValue,
                    visibility: storyQuestion?.visibility || "private_access",
                    status: storyQuestion?.status || "active"
                  }
                }
              >
                {error && <ErrorMessage error={error} />}
                <div className={styles["prompt-header"]}>
                  <div className={styles["prompt-header__info"]}>
                    <div className={styles["prompt-header__info-icon"]}>
                      <img style={{ width: "24px", height: "24px" }} src={magicWandIcon} alt="Magic Wand" />
                    </div>
                    <div className={styles["prompt-description"]}>
                      <span className="chq-ffd--lb"><Trans>What are you looking to do?</Trans></span>
                      <p>{promptDescription}</p>
                    </div>
                  </div>
                  <div className="generate-again">
                    <Button
                      className={styles["generate-again__button"]}
                      inverted
                      onClick={handleReloadPrompt}
                    >
                      <MagicWandIcon /> <Trans>Generate again</Trans>
                    </Button>
                  </div>
                </div>
                <hr className={styles.divider} />
                <StringField name="prompt" autoFocus required onChange={(value: any) => { setPrompt(value); onChange(); }} value={prompt}>
                  <Trans>Prompt</Trans>
                </StringField>
                <TextField
                  name="summary"
                  placeholder={t("Share details about what you're looking for and the purpose of this prompt so users know why they should contribute")}
                  required
                  onChange={(value: any) => { setSummary(value); onChange(); }}
                  value={summary}
                >
                  <Trans>Summary</Trans>
                </TextField>
                <AIQuestionOptions
                  questions={questions}
                  filteredQuestions={filteredQuestions}
                  onDelete={handleRemoveQuestion}
                  onUpdate={handleEditQuestion}
                  onRefresh={handleReloadQuestions}
                  onSortQuestions={handleSortQuestions}
                />
                {allowAddQuestion && (
                  <AIQuestionModal
                    chatId={chatId}
                    onCreate={handleNewQuestion}
                  >
                    {(onTrigger: any) => (
                      <Button className={classnames(styles["question-add-btn"], { EmptySubPrompts: isFilteredQuestionsEmpty })} inverted onClick={onTrigger}>
                        <Icon icon="ios-plus" />
                        <Trans>
                          {
                            isFilteredQuestionsEmpty
                              ? "Add Your First Question"
                              : "Add Another Question"
                          }
                        </Trans>
                      </Button>
                    )}
                  </AIQuestionModal>
                )}
                <SelectField
                  className="AllowedTypes"
                  options={allowedMedia}
                  name="allowedTypes"
                  required
                  onChange={onChange}
                >
                  <Trans>Media Types Allowed</Trans>
                </SelectField>
                <NumberField
                  name="points"
                  placeholder={t("Choose how much users should earn for contributing")}
                  required
                  onChange={(value: string) => {
                    const numericValue = parseInt(value, 10);
                    if (!Number.isNaN(numericValue)) {
                      if ((numericValue >= GAMIFICATION_MIN)
                        && (numericValue <= GAMIFICATION_MAX)) {
                        setGamificationValue(numericValue);
                        onChange();
                      }
                    }
                  }}
                  min={GAMIFICATION_MIN}
                  max={GAMIFICATION_MAX}
                  value={gamificationValue.toString()}
                >
                  <Trans>Gamification</Trans>
                </NumberField>
                <SelectField
                  name="visibility"
                  options={[
                    { value: "private_access", label: t("Private") },
                    { value: "public_access", label: t("Public") }
                  ]}
                  onChange={onChange}
                >
                  <Trans>Visibility</Trans>
                </SelectField>
                <SelectField
                  name="status"
                  options={[
                    { value: "active", label: t("Active") },
                    { value: "paused", label: t("Paused") }
                  ]}
                  onChange={onChange}
                >
                  <Trans>Status</Trans>
                </SelectField>
                <div className="Footer-Buttons">
                  <SubmitButton primary>
                    {submitting => (
                      submitting
                        ? <Trans>Submitting...</Trans>
                        : <SaveButton storyQuestion={storyQuestion} />
                    )}
                  </SubmitButton>
                  <ApproveForJobMappingButton
                    mappersValueIds={mappersValueIds}
                    storyQuestion={storyQuestion}
                    onUpdate={(value: Record<string, string[]> | Story | ApprovedForSocial) => {
                      handleSelectedJobMappings(value as Record<string, string[]>);
                    }}
                  />
                </div>
              </Form>
            )}
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default AIStoryQuestionModalForm;
